import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, PartyPopper, Phone, Mail, CreditCard, Palette, ShieldCheck, Upload, X } from "lucide-react";
import { Reveal } from "../Reveal";
import { PACKAGES, DESIGN_FEE, PAYMENT_LINKS, CONTACT } from "../../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
    first_name: "", last_name: "", business_name: "", phone: "", email: "",
    business_type: "", advertising_what: "", start_date: "", end_date: "",
    campaign_length: "", budget: "", location: "", has_artwork: "", needs_design: "",
    consent_email: false, message: "", package_id: "",
};

const KEY_TERMS = [
    "Per-billboard pricing",
    "7 words or less + logo",
    "Approval before going live",
    "Turnaround times may vary",
    "All sales final — no refunds",
    "$50 design fee non-refundable once work begins",
];

const Field = ({ label, required, error, children }) => (
    <div>
        <label className="mb-1.5 block font-alt text-[10px] font-bold tracking-[0.2em] text-zinc-300 uppercase">
            {label}{required && <span className="text-hotpink"> *</span>}
        </label>
        {children}
        {error && <p className="mt-1.5 text-xs text-rose-400" role="alert">{error}</p>}
    </div>
);

const YesNo = ({ value, onChange, yesLabel = "YES", noLabel = "NO", testid }) => (
    <div className="grid grid-cols-2 gap-3">
        {[{ v: "yes", label: yesLabel }, { v: "no", label: noLabel }].map((o) => (
            <button
                key={o.v}
                type="button"
                onClick={() => onChange(o.v)}
                className={`rounded-xl border px-4 py-3.5 font-alt text-[11px] font-bold tracking-[0.1em] transition-all duration-200 ${
                    value === o.v
                        ? "border-hotpink bg-hotpink/15 text-white led-glow"
                        : "border-white/15 bg-white/[0.05] text-zinc-300 hover:border-white/35"
                }`}
                data-testid={`${testid}-${o.v}`}
            >
                {o.label}
            </button>
        ))}
    </div>
);

export const FunnelQuote = ({ prefill }) => {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [artwork, setArtwork] = useState([]);
    const fileRef = useRef(null);

    const addFiles = async (fileList) => {
        const remaining = 5 - artwork.length;
        const files = Array.from(fileList || []).slice(0, Math.max(remaining, 0));
        if ((fileList || []).length > remaining) toast.error("You can attach up to 5 files.");
        for (const file of files) {
            if (!/image\/(png|jpeg)/.test(file.type)) { toast.error(`${file.name}: only PNG or JPG files are accepted.`); continue; }
            if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name}: exceeds the 15MB limit.`); continue; }
            const entry = { id: `${Date.now()}-${file.name}`, filename: file.name, preview: URL.createObjectURL(file), path: "", uploading: true };
            setArtwork((a) => [...a, entry]);
            try {
                const fd = new FormData();
                fd.append("file", file);
                const res = await axios.post(`${API}/upload`, fd);
                setArtwork((a) => a.map((x) => (x.id === entry.id ? { ...x, path: res.data.storage_path, uploading: false } : x)));
            } catch (err) {
                toast.error(err.response?.data?.detail || `${file.name}: upload failed.`);
                setArtwork((a) => a.filter((x) => x.id !== entry.id));
            }
        }
        if (fileRef.current) fileRef.current.value = "";
    };
    const [quoteId, setQuoteId] = useState(null);
    const topRef = useRef(null);

    useEffect(() => {
        if (prefill?.packageId) {
            setForm((f) => ({ ...f, package_id: prefill.packageId }));
        }
    }, [prefill]);

    const pkg = useMemo(() => PACKAGES.find((p) => p.id === form.package_id), [form.package_id]);

    const set = (key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.first_name.trim()) e.first_name = "First name is required.";
        if (!form.last_name.trim()) e.last_name = "Last name is required.";
        if (!form.business_name.trim()) e.business_name = "Business name is required.";
        if (!form.phone.trim()) e.phone = "Phone is required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "A valid email is required.";
        if (!form.business_type.trim()) e.business_type = "Business type is required.";
        if (!form.advertising_what.trim()) e.advertising_what = "Tell us what you are advertising.";
        if (!form.start_date) e.start_date = "Start date is required.";
        if (!form.end_date) e.end_date = "End date is required.";
        if (form.start_date && form.end_date && form.end_date < form.start_date) e.end_date = "End date must be after start date.";
        if (!form.campaign_length) e.campaign_length = "Select a campaign length.";
        if (!form.budget) e.budget = "Select your advertising budget.";
        if (!form.location.trim()) e.location = "Desired location is required.";
        if (!form.has_artwork) e.has_artwork = "Please select yes or no.";
        if (!form.needs_design) e.needs_design = "Please select yes or no.";
        if (!form.consent_email) e.consent_email = "Email consent is required so we can respond to your inquiry.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async () => {
        if (!validate()) {
            toast.error("Please complete the required fields.");
            topRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        if (artwork.some((f) => f.uploading)) {
            toast.error("Please wait for your artwork to finish uploading.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                has_artwork: form.has_artwork === "yes",
                needs_design: form.needs_design === "yes",
                package_id: pkg?.id || "",
                package_name: pkg?.name || "",
                package_price: pkg?.price || 0,
                artwork_files: artwork.filter((f) => f.path).map((f) => f.path),
            };
            const res = await axios.post(`${API}/leads`, payload);
            setQuoteId(res.data?.lead_id || null);
            setSubmitted(true);
            topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Submission failed. Please try again or call us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls = (key) => `input-light ${errors[key] ? "input-error" : ""}`;

    return (
        <section id="quote" className="relative bg-noir py-24 sm:py-32 overflow-hidden" data-testid="funnel-quote">
            <div className="absolute inset-0 led-dots opacity-30" aria-hidden="true" />
            <div className="absolute -top-24 left-1/3 h-[420px] w-[420px] rounded-full bg-hotpink/12 blur-[130px]" aria-hidden="true" />
            <div ref={topRef} className="absolute -top-16" />
            <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6">
                <div className="text-center max-w-3xl mx-auto mb-12">
                    <Reveal>
                        <p className="eyebrow">REQUEST A QUOTE</p>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02] tracking-tight text-white" data-testid="quote-headline">
                            LET'S PUT YOUR BRAND ON <span className="text-hotpink led-text-glow">THE BIG SCREEN.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <p className="mt-5 text-sm sm:text-base text-zinc-400">
                            Tell us about your campaign — our team will review your details and follow up with your
                            quote and available placement options.
                        </p>
                    </Reveal>
                </div>

                {submitted ? (
                    <Reveal>
                        <div className="mx-auto max-w-2xl text-center rounded-3xl border-2 border-hotpink/60 bg-noir-elevated p-10 sm:p-12 led-glow" data-testid="quote-confirmation">
                            <PartyPopper size={42} className="mx-auto text-hotpink" />
                            <h3 className="mt-6 font-display text-3xl sm:text-4xl font-extrabold text-white">
                                YOUR QUOTE REQUEST IS IN!
                            </h3>
                            <p className="mx-auto mt-5 max-w-lg text-sm leading-relaxed text-zinc-300">
                                Thank you for choosing WealthHaus Media. Our team will review your campaign details and
                                follow up with your quote and available placement options. Artwork must be approved before
                                your advertisement can go live — turnaround times may vary.
                            </p>

                            {pkg && (
                                <div className="mx-auto mt-8 max-w-md rounded-2xl border border-white/15 bg-white/[0.05] p-6" data-testid="quote-payment-panel">
                                    <p className="font-alt text-[10px] font-bold tracking-[0.26em] text-hotpink">WANT TO MOVE FAST?</p>
                                    <p className="mt-2 text-xs text-zinc-400 leading-relaxed">
                                        Lock in your selected campaign option now — payment is required before campaign
                                        setup, creative production, placement processing, and/or placement.
                                    </p>
                                    <a
                                        href={`${PAYMENT_LINKS[pkg.id]}?client_reference_id=${quoteId || ""}&prefilled_email=${encodeURIComponent(form.email)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-glossy-pink mt-5 w-full !text-[11px]"
                                        data-testid="quote-payment-button"
                                    >
                                        <CreditCard size={15} /> PAY — {pkg.name} (${pkg.price} PER BILLBOARD)
                                    </a>
                                    {form.needs_design === "yes" && (
                                        <a
                                            href={`${PAYMENT_LINKS.design}?client_reference_id=${quoteId || ""}&prefilled_email=${encodeURIComponent(form.email)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-chrome mt-3 w-full !text-[11px]"
                                            data-testid="quote-design-payment-button"
                                        >
                                            <Palette size={14} /> ADD CUSTOM DESIGN (${DESIGN_FEE} PER BILLBOARD)
                                        </a>
                                    )}
                                    <p className="mt-4 flex items-center justify-center gap-1.5 font-alt text-[10px] font-semibold tracking-[0.16em] text-zinc-500">
                                        <ShieldCheck size={12} className="text-hotpink" /> SECURE CHECKOUT BY STRIPE
                                    </p>
                                </div>
                            )}

                            <div className="mt-8 border-t border-white/10 pt-6 flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-zinc-300">
                                <a href={CONTACT.phoneHref} className="flex items-center gap-2 hover:text-white" data-testid="quote-confirm-phone"><Phone size={15} className="text-hotpink" /> {CONTACT.phone}</a>
                                <a href={CONTACT.mailHref} className="flex items-center gap-2 hover:text-white" data-testid="quote-confirm-email"><Mail size={15} className="text-hotpink" /> {CONTACT.email}</a>
                            </div>
                        </div>
                    </Reveal>
                ) : (
                    <Reveal>
                        <div className="mx-auto max-w-3xl">
                            <div className="mb-6 flex flex-wrap justify-center gap-2" data-testid="quote-terms-strip">
                                {KEY_TERMS.map((t) => (
                                    <span key={t} className="rounded-full border border-white/12 bg-white/[0.05] px-3.5 py-1.5 font-alt text-[10px] font-semibold tracking-wider text-zinc-300">
                                        {t}
                                    </span>
                                ))}
                            </div>

                            <div className="rounded-3xl border border-white/12 bg-noir-elevated/80 backdrop-blur-xl p-6 sm:p-10 shadow-[0_30px_90px_rgba(0,0,0,0.6)]" data-testid="quote-form">
                                {pkg && (
                                    <div className="mb-7 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-hotpink/50 bg-hotpink/[0.08] px-5 py-3.5" data-testid="quote-package-chip">
                                        <span className="font-alt text-[11px] font-bold tracking-[0.14em] text-white">
                                            SELECTED: {pkg.name} — ${pkg.price} PER BILLBOARD
                                        </span>
                                        <button onClick={() => set("package_id", "")} className="font-alt text-[10px] font-semibold tracking-wider text-zinc-400 hover:text-white" data-testid="quote-package-clear">
                                            CHANGE
                                        </button>
                                    </div>
                                )}

                                <div className="grid sm:grid-cols-2 gap-5">
                                    <Field label="First Name" required error={errors.first_name}>
                                        <input className={inputCls("first_name")} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="First name" data-testid="quote-first-name" />
                                    </Field>
                                    <Field label="Last Name" required error={errors.last_name}>
                                        <input className={inputCls("last_name")} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Last name" data-testid="quote-last-name" />
                                    </Field>
                                    <Field label="Business Name" required error={errors.business_name}>
                                        <input className={inputCls("business_name")} value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Your business name" data-testid="quote-business-name" />
                                    </Field>
                                    <Field label="Phone" required error={errors.phone}>
                                        <input className={inputCls("phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(___) ___-____" data-testid="quote-phone" />
                                    </Field>
                                    <Field label="Email" required error={errors.email}>
                                        <input type="email" className={inputCls("email")} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" data-testid="quote-email" />
                                    </Field>
                                    <Field label="Business Type" required error={errors.business_type}>
                                        <input className={inputCls("business_type")} value={form.business_type} onChange={(e) => set("business_type", e.target.value)} placeholder="e.g. Restaurant, Real Estate, Salon" data-testid="quote-business-type" />
                                    </Field>
                                    <div className="sm:col-span-2">
                                        <Field label="What Are You Advertising?" required error={errors.advertising_what}>
                                            <input className={inputCls("advertising_what")} value={form.advertising_what} onChange={(e) => set("advertising_what", e.target.value)} placeholder="e.g. Grand opening, new product, my brand" data-testid="quote-advertising-what" />
                                        </Field>
                                    </div>
                                    <Field label="Campaign Start Date" required error={errors.start_date}>
                                        <input type="date" className={inputCls("start_date")} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="quote-start-date" />
                                    </Field>
                                    <Field label="Campaign End Date" required error={errors.end_date}>
                                        <input type="date" className={inputCls("end_date")} value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="quote-end-date" />
                                    </Field>
                                    <Field label="Desired Campaign Length" required error={errors.campaign_length}>
                                        <select className={inputCls("campaign_length")} value={form.campaign_length} onChange={(e) => set("campaign_length", e.target.value)} data-testid="quote-campaign-length">
                                            <option value="">Select campaign length</option>
                                            <option>2-Day Campaign</option>
                                            <option>5-Day Campaign</option>
                                            <option>7-Day Campaign</option>
                                            <option>Custom / Extended Campaign</option>
                                            <option>Not Sure Yet</option>
                                        </select>
                                    </Field>
                                    <Field label="Advertising Budget" required error={errors.budget}>
                                        <select className={inputCls("budget")} value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="quote-budget">
                                            <option value="">Select your budget</option>
                                            <option>Under $250</option>
                                            <option>$250 – $500</option>
                                            <option>$500 – $1,000</option>
                                            <option>$1,000+</option>
                                            <option>Not Sure Yet</option>
                                        </select>
                                    </Field>
                                    <div className="sm:col-span-2">
                                        <Field label="Desired Location" required error={errors.location}>
                                            <input className={inputCls("location")} value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City, state, or area where you want to be seen" data-testid="quote-location" />
                                        </Field>
                                    </div>
                                    <div className="sm:col-span-2 grid sm:grid-cols-2 gap-5">
                                        <Field label="Do You Already Have Billboard Artwork?" required error={errors.has_artwork}>
                                            <YesNo value={form.has_artwork} onChange={(v) => set("has_artwork", v)} testid="quote-artwork" />
                                        </Field>
                                        <Field label="Do You Need Help Creating Your Advertisement?" required error={errors.needs_design}>
                                            <YesNo value={form.needs_design} onChange={(v) => set("needs_design", v)} yesLabel={`YES (+$${DESIGN_FEE}/BILLBOARD)`} noLabel="NO" testid="quote-needs-design" />
                                        </Field>
                                    </div>
                                    {form.has_artwork === "yes" && (
                                        <div className="sm:col-span-2">
                                            <Field label="Upload Your Artwork or Logo (optional)">
                                                <input ref={fileRef} type="file" accept="image/png,image/jpeg" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="quote-artwork-input" />
                                                <button
                                                    type="button"
                                                    onClick={() => fileRef.current?.click()}
                                                    className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-dashed border-white/25 bg-white/[0.04] px-4 py-5 font-alt text-[11px] font-bold tracking-[0.14em] text-zinc-300 hover:border-hotpink/60 hover:text-white transition-colors"
                                                    data-testid="quote-artwork-upload"
                                                >
                                                    <Upload size={15} /> ADD PNG OR JPG FILES (MAX 15MB EACH)
                                                </button>
                                                {artwork.length > 0 && (
                                                    <div className="mt-3 grid grid-cols-3 gap-2.5" data-testid="quote-artwork-list">
                                                        {artwork.map((f, i) => (
                                                            <div key={f.id} className="relative rounded-lg overflow-hidden border border-white/15 bg-noir">
                                                                <img src={f.preview} alt={f.filename} className="h-20 w-full object-cover" />
                                                                {f.uploading && (
                                                                    <div className="absolute inset-0 flex items-center justify-center bg-noir/70">
                                                                        <Loader2 size={16} className="animate-spin text-white" />
                                                                    </div>
                                                                )}
                                                                <button type="button" onClick={() => setArtwork((a) => a.filter((x) => x.id !== f.id))} className="absolute top-1 right-1 rounded-full bg-noir/80 p-1 text-zinc-300 hover:text-white" aria-label={`Remove ${f.filename}`} data-testid={`quote-artwork-remove-${i}`}>
                                                                    <X size={12} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </Field>
                                        </div>
                                    )}
                                    {!pkg && (
                                        <div className="sm:col-span-2">
                                            <Field label="Campaign Option (Optional)" error={errors.package_id}>
                                                <select className={inputCls("package_id")} value={form.package_id} onChange={(e) => set("package_id", e.target.value)} data-testid="quote-package-select">
                                                    <option value="">Not sure yet — recommend an option</option>
                                                    {PACKAGES.map((p) => (
                                                        <option key={p.id} value={p.id}>{p.name} — ${p.price} per billboard ({p.duration})</option>
                                                    ))}
                                                </select>
                                            </Field>
                                        </div>
                                    )}
                                    <div className="sm:col-span-2">
                                        <Field label="Message" error={errors.message}>
                                            <textarea rows={4} className={inputCls("message")} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Anything else we should know — schedule preferences, number of billboards, questions…" data-testid="quote-message" />
                                        </Field>
                                    </div>
                                </div>

                                <div className="mt-7 space-y-3.5">
                                    <label className="flex items-start gap-3 rounded-xl border border-white/12 bg-white/[0.04] p-4 cursor-pointer hover:border-hotpink/50 transition-colors" data-testid="consent-email">
                                        <input
                                            type="checkbox"
                                            className="mt-0.5 h-4 w-4 shrink-0 accent-[#FFFFFF] cursor-pointer"
                                            checked={form.consent_email}
                                            onChange={(e) => set("consent_email", e.target.checked)}
                                        />
                                        <span className="text-xs leading-relaxed text-zinc-300">
                                            I agree to receive email communications from WealthHaus Media regarding my
                                            inquiry and advertising opportunities. <span className="text-hotpink">*</span>
                                        </span>
                                    </label>
                                    {errors.consent_email && <p className="text-xs text-rose-400" role="alert">{errors.consent_email}</p>}
                                </div>

                                <button
                                    type="button"
                                    onClick={submit}
                                    disabled={submitting}
                                    className="btn-glossy-pink mt-8 w-full !py-4 !text-sm disabled:opacity-60"
                                    data-testid="quote-submit-button"
                                >
                                    {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                                    {submitting ? "SENDING…" : "GET MY QUOTE"} <ArrowRight size={16} />
                                </button>
                                <p className="mt-4 text-center text-[11px] leading-relaxed text-zinc-500">
                                    Submitting a quote request does not guarantee a specific billboard, screen, location,
                                    schedule, impression count, or placement. All placements are subject to availability
                                    and confirmation.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                )}
            </div>
        </section>
    );
};
